/**
 * Pack Size Selector
 *
 * Manages the custom UI-only Pack Size picker rendered by
 * snippets/pack-size-selector.liquid. Emits a `packSizeChanged` DOM event so
 * other modules (custom.js for price/savings display, product-form.js for
 * cart submission) can react without knowing about variant IDs.
 *
 * Shared globals set on window:
 * - window._currentPackCount    : currently selected pack count (1, 2, or 4)
 * - window._onePackBasePrice    : 1-Pack price in cents, from data attribute
 */
(function () {
  'use strict';

  function readOnePackPrice(container) {
    const raw = container.dataset.onePackPrice;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * Returns true if the given radio input is marked as out-of-stock
   * (has the .disabled class set by pack-size-selector.liquid).
   */
  function isPackUnavailable(input) {
    return input.classList.contains('disabled');
  }

  /**
   * Update the main Add to Cart button and variant hidden input to reflect
   * whether the currently selected pack is available or sold out.
   */
  function updateSubmitButtonState(available) {
    const submitBtn = document.querySelector('button[id^="ProductSubmitButton-"]:not([id^="StickySubmitButton-"])');
    if (!submitBtn) return;

    const btnLabel = submitBtn.querySelector('span:not(.loading__spinner):not(.Add-tocart-text)');
    const variantInput = document.querySelector('input.product-variant-id[name="id"]');
    const notifyBtn = document.getElementById('notify-me-btn');

    if (available) {
      submitBtn.removeAttribute('disabled');
      submitBtn.removeAttribute('aria-disabled');
      submitBtn.classList.remove('product_sold-out');
      if (btnLabel) btnLabel.textContent = 'Add to Cart';
      if (variantInput) variantInput.removeAttribute('disabled');
      if (notifyBtn) notifyBtn.classList.add('hidden');
    } else {
      submitBtn.setAttribute('disabled', 'disabled');
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('product_sold-out');
      if (btnLabel) btnLabel.textContent = 'Sold Out';
      if (variantInput) variantInput.setAttribute('disabled', 'disabled');
      if (notifyBtn) notifyBtn.classList.remove('hidden');
    }

    // Let syncStickyButtonWithMain (custom.js) mirror the new state to the sticky bar
    if (typeof window.updateStickyAddToCartBar === 'function') {
      window.updateStickyAddToCartBar({ available });
    }
  }

  function initSelector(container) {
    if (!container || container.dataset.packSizeInitialized === 'true') return;
    container.dataset.packSizeInitialized = 'true';

    window._onePackBasePrice = readOnePackPrice(container);

    const checked = container.querySelector('input[name="pack_size"]:checked');
    const initialPackCount = checked ? parseInt(checked.value, 10) : 1;
    window._currentPackCount = initialPackCount;

    // Set initial button state based on whether the default pack is available
    if (checked) updateSubmitButtonState(!isPackUnavailable(checked));

    container.addEventListener('change', (event) => {
      if (!event.target || event.target.name !== 'pack_size') return;
      const packCount = parseInt(event.target.value, 10) || 1;
      window._currentPackCount = packCount;

      updateSubmitButtonState(!isPackUnavailable(event.target));

      document.dispatchEvent(
        new CustomEvent('packSizeChanged', {
          detail: { packCount, onePackPrice: window._onePackBasePrice },
        })
      );
    });
  }

  function init() {
    document.querySelectorAll('pack-size-selector').forEach(initSelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-sync window._currentPackCount after back-navigation.
  // Browser form-state restoration (which sets the 2-Pack radio as checked)
  // can happen concurrently with or after DOMContentLoaded, leaving the
  // cached count stale. pageshow fires on every page entry; setTimeout(0)
  // lets the browser complete restoration before we read the radio.
  window.addEventListener('pageshow', function () {
    setTimeout(function () {
      document.querySelectorAll('pack-size-selector').forEach(function (container) {
        const checked = container.querySelector('input[name="pack_size"]:checked');
        if (!checked) return;
        const packCount = parseInt(checked.value, 10) || 1;
        if (packCount === window._currentPackCount) return;
        window._currentPackCount = packCount;
        updateSubmitButtonState(!isPackUnavailable(checked));
        document.dispatchEvent(
          new CustomEvent('packSizeChanged', {
            detail: { packCount, onePackPrice: window._onePackBasePrice },
          })
        );
      });
    }, 0);
  });
})();
